import { Palette, PaletteMode } from '@mui/material';
import '@mui/material/styles';
import { alpha, PaletteColor, PaletteOptions } from '@mui/material/styles';
import { merge } from 'lodash-es';

export type ColorSchema =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'purple'
  | 'violet'
  | 'rose'
  | 'default'
  | undefined;

export enum COLORS_SCHEMA {
  PRIMARY = 'primary',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  PURPLE = 'purple',
  VIOLET = 'violet',
  ROSE = 'rose',
  DEFAULT = 'default',
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }

  interface PaletteColor {
    25: string;
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteOptions {
    default?: PaletteColor;
    white?: PaletteColor;
    purple?: PaletteColor;
    violet?: PaletteColor;
    rose?: PaletteColor;
  }

  interface Palette {
    default: PaletteColor;
    white?: PaletteColor;
    purple: PaletteColor;
    violet: PaletteColor;
    rose: PaletteColor;
  }
}

// SETUP COLORS

const WHITE: PaletteColor = {
  25: '#FFFFFF',
  50: '#FFFFFF',
  100: '#FFFFFF',
  200: '#FFFFFF',
  300: '#FFFFFF',
  400: '#FFFFFF',
  500: '#FFFFFF',
  600: '#FFFFFF',
  700: '#FFFFFF',
  800: '#FFFFFF',
  900: '#FFFFFF',
  contrastText: '#000',
  light: '#FFFFFF',
  main: '#FFFFFF',
  dark: '#FFFFFF',
};

const GREY: PaletteColor = {
  25: '#FCFCFD',
  50: '#F9FAFB',
  100: '#F4F6F8', // todo: check with Saman // 100 and 200 are the same color
  200: '#F4F6F8', // todo: check with Saman // 100 and 200 are the same color
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1D2939',
  900: '#101828',
  contrastText: '#fff',
  light: '#F4F6F8',
  main: '#667085',
  dark: '#475467',
};

const PRIMARY: PaletteColor = {
  25: '#FCFAFF',
  50: '#F1F4FF',
  100: '#CFDBFC',
  200: '#A0B7F8',
  300: '#7093F5',
  400: '#406FF2',
  500: '#1851EF',
  600: '#0D3CBF',
  700: '#0A2D8F',
  800: '#071E5F',
  900: '#030F30',
  contrastText: '#FFFFFF',
  light: '#CFDBFC',
  main: '#1851EF',
  dark: '#0D3CBF',
};

const SUCCESS: PaletteColor = {
  25: '#F6FEF9',
  50: '#ECFDF3',
  100: '#D1FADF',
  200: '#A6F4C5',
  300: '#6CE9A6',
  400: '#32D583',
  500: '#12B76A',
  600: '#039855',
  700: '#027A48',
  800: '#05603A',
  900: '#054F31',
  contrastText: '#ffffff',
  light: '#D1FADF',
  main: '#12B76A',
  dark: '#039855',
};

const WARNING: PaletteColor = {
  25: '#FFFCF5',
  50: '#FFFAEB',
  100: '#FEF0C7',
  200: '#FEDF89',
  300: '#FEC84B',
  400: '#FDB022',
  500: '#F79009',
  600: '#DC6803',
  700: '#B54708',
  800: '#93370D',
  900: '#7A2E0E',
  contrastText: '#ffffff',
  light: '#FEF0C7',
  main: '#F79009',
  dark: '#DC6803',
};

const ERROR: PaletteColor = {
  25: '#FFFBFA',
  50: '#FEF3F2',
  100: '#FEE4E2',
  200: '#FECDCA',
  300: '#FDA29B',
  400: '#F97066',
  500: '#F04438',
  600: '#D92D20',
  700: '#B42318',
  800: '#912018',
  900: '#7A271A',
  contrastText: '#FFFFFF',
  light: '#FEE4E2',
  main: '#F04438',
  dark: '#D92D20',
};

const PURPLE: PaletteColor = {
  25: '#F4EBF7',
  50: '#E8D8EF',
  100: '#DDC4E6',
  200: '#D1B1DE',
  300: '#BB89CE',
  400: '#A462BD',
  500: '#8D3BAD',
  600: '#712F8A',
  700: '#552368',
  800: '#381845',
  900: '#2A1234',
  contrastText: '#FFFFFF',
  light: '#D1B1DE',
  main: '#8D3BAD',
  dark: '#712F8A',
};

const VIOLET: PaletteColor = {
  25: '#F5F3FF',
  50: '#EDE9FE',
  100: '#DDD6FE',
  200: '#C4B5FD',
  300: '#A78BFA',
  400: '#8B5CF6',
  500: '#7C3AED',
  600: '#6D28D9',
  700: '#5B21B6',
  800: '#4C1D95',
  900: '#2E1065',
  contrastText: '#FFFFFF',
  light: '#C4B5FD',
  main: '#7C3AED',
  dark: '#6D28D9',
};

const ROSE: PaletteColor = {
  25: '#FFF1F2',
  50: '#FFE4E6',
  100: '#FECDD3',
  200: '#FDA4AF',
  300: '#FB7185',
  400: '#F43F5E',
  500: '#E11D48',
  600: '#BE123C',
  700: '#9F1239',
  800: '#881337',
  900: '#4C0519',
  contrastText: '#FFFFFF',
  light: '#FDA4AF',
  main: '#E11D48',
  dark: '#BE123C',
};

const COMMON: Omit<PaletteOptions, 'mode' | 'text' | 'background'> = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  white: WHITE,
  primary: PRIMARY,
  info: PRIMARY,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  purple: PURPLE,
  violet: VIOLET,
  rose: ROSE,
  grey: GREY,
  default: GREY,
  divider: GREY[300],
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: GREY[100],
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    activatedOpacity: 0.24,
    active: GREY[500],
    focusOpacity: 0.12,
    selectedOpacity: 0.16,
  },
};

export function palette(mode: PaletteMode): Palette {
  const light = {
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: GREY[200],
    },
    action: merge(COMMON.action, { active: GREY[600] }),
  };

  const dark = {
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: merge(COMMON.action, { active: GREY[500] }),
  };
  const modeProperties =
    mode === 'dark' ? (dark as Palette) : (light as Palette);

  return merge(modeProperties, COMMON);
}
